import * as ort from 'onnxruntime-web';
import {
  ACTION_COUNT,
  BOARD_H,
  BOARD_W,
  GLOBAL_SIZE,
  SPATIAL_CHANNELS,
} from './env/constants';
import { actionMask } from './env/actions';
import { encodeState } from './env/encode';
import { decodeActionIndex } from './env/actions';
import type { BlindKingsGame } from '@blind-kings/game-core';
import {
  resolveBotDifficulty,
  type BotDifficultyConfig,
  type BotDifficultyId,
} from './difficulty';
import { argmaxMasked, sampleMaskedTopP } from './sample';

const DEFAULT_MODEL_URL = '/models/v2.onnx';

let sessionPromise: Promise<ort.InferenceSession> | null = null;

function configureOrt() {
  // Serve WASM from CDN so Vite/dev don't need extra copy steps.
  ort.env.wasm.wasmPaths =
    'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/';
}

async function getSession(modelUrl = DEFAULT_MODEL_URL): Promise<ort.InferenceSession> {
  if (!sessionPromise) {
    configureOrt();
    sessionPromise = ort.InferenceSession.create(modelUrl, {
      executionProviders: ['wasm'],
    });
  }
  return sessionPromise;
}

export type BotActionPayload = {
  action: string;
  pieceId: number;
  direction?: string;
};

export async function pickBotAction(
  game: BlindKingsGame,
  difficulty: BotDifficultyId | string = 'hard',
  modelUrl = DEFAULT_MODEL_URL
): Promise<BotActionPayload> {
  const cfg: BotDifficultyConfig = resolveBotDifficulty(difficulty);
  const { spatial, global } = encodeState(game);
  const mask = actionMask(game);
  const session = await getSession(modelUrl);

  const spatialTensor = new ort.Tensor('float32', spatial, [
    1,
    SPATIAL_CHANNELS,
    BOARD_H,
    BOARD_W,
  ]);
  const globalTensor = new ort.Tensor('float32', global, [1, GLOBAL_SIZE]);
  const out = await session.run({
    spatial: spatialTensor,
    global: globalTensor,
  });

  const keys = Object.keys(out);
  const logitsKey =
    keys.find((k) => k.includes('policy') || k === 'policy_logits') ?? keys[0];
  const logits = out[logitsKey].data as Float32Array;
  if (logits.length < ACTION_COUNT) {
    throw new Error(`Unexpected policy length ${logits.length}`);
  }

  const index = cfg.greedy
    ? argmaxMasked(logits, mask)
    : sampleMaskedTopP(logits, mask, cfg.temperature, cfg.topP);
  const decoded = decodeActionIndex(game, index);
  return {
    action: decoded.type,
    pieceId: decoded.pieceId,
    ...(decoded.direction ? { direction: decoded.direction } : {}),
  };
}

/** Prefetch the model so the first bot turn is snappy. */
export function preloadBotModel(modelUrl = DEFAULT_MODEL_URL): void {
  void getSession(modelUrl).catch((err) => {
    console.warn('[bot] model preload failed', err);
  });
}
